import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/solid';

const RentReviewCalculatorLandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: 'HICP-Based Calculations',
      description: 'Automatically calculates allowable increases based on official HICP data',
      icon: '📈',
    },
    {
      title: 'RPZ Compliance',
      description: 'Ensures your rent increase complies with Rent Pressure Zone regulations',
      icon: '✅',
    },
    {
      title: 'Real-Time Data',
      description: 'Uses current Irish inflation rates and RPZ boundaries',
      icon: '🕐',
    },
    {
      title: 'Clear Breakdown',
      description: 'See exactly what increase is allowed and what it means for your tenant',
      icon: '📊',
    },
    {
      title: 'Tenant Communication',
      description: 'Generate formal notice templates for rent reviews',
      icon: '📝',
    },
    {
      title: 'Legal Compliance',
      description: 'Stays compliant with Irish rental laws and regulations',
      icon: '⚖️',
    },
  ];

  const benefits = [
    'Stay compliant with Irish rental regulations',
    'Calculate allowable increases accurately',
    'Avoid costly legal disputes',
    'Understand RPZ restrictions for your property',
    'Generate professional notice documents',
  ];

  const faqItems = [
    {
      question: 'Does the calculator use the latest HICP data?',
      answer:
        'Yes. The calculator references current Harmonised Index of Consumer Prices data to ensure accurate allowable increases.',
    },
    {
      question: 'Is it compliant with Rent Pressure Zone rules?',
      answer:
        'It checks RPZ status and caps increases to the lower of 2% annually or HICP, in line with Irish regulations.',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <title>
          Rent Review Calculator Ireland | Calculate Legal Rent Increases | RentSmart.ie
        </title>
        <meta
          name="description"
          content="Calculate legal rent increases with RentSmart's Rent Review Calculator. HICP-based calculations, RPZ compliant. Perfect for Irish landlords managing rent reviews."
        />
        <meta
          name="keywords"
          content="rent review calculator, HICP, RPZ, rent increase, Ireland landlord laws, Rent Pressure Zones, rental regulations, rent increment calculator"
        />
        <link
          rel="canonical"
          href="https://rentsmart.ie/property-tools/rent-review-calculator-landing"
        />
        <meta property="og:title" content="Rent Review Calculator - RentSmart.ie" />
        <meta
          property="og:description"
          content="Calculate legal rent increases with HICP data and RPZ compliance using RentSmart's free Rent Review Calculator."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://rentsmart.ie/property-tools/rent-review-calculator-landing"
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Legally Calculate Your
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                Rent Review
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Use HICP data and RPZ compliance rules to calculate your allowable rent increase. Stay
              compliant with Irish rental laws.
            </p>
            <button
              onClick={() => navigate('/landlord-toolkit')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Calculate Your Rent Review
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Compliant Rent Management
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Use Rent Review Calculator?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircleIcon className="w-6 h-6 text-purple-500 flex-shrink-0 mt-1" />
                  <p className="text-lg text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RPZ Information */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-purple-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              Understanding RPZ & HICP
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-2xl font-semibold text-purple-600 mb-3">
                  📍 Rent Pressure Zones
                </h3>
                <p className="text-gray-700 mb-4">
                  Rent increases in Rent Pressure Zones are capped at 2% per year or HICP, whichever
                  is lower.
                </p>
                <p className="text-gray-600 text-sm">
                  Our calculator automatically detects if your property is in an RPZ and applies the
                  correct limits.
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="text-2xl font-semibold text-purple-600 mb-3">📊 HICP Inflation</h3>
                <p className="text-gray-700 mb-4">
                  Outside RPZs, you can increase rent by up to HICP (Harmonised Indices of Consumer
                  Prices).
                </p>
                <p className="text-gray-600 text-sm">
                  We use official data to ensure your calculations are always current and accurate.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: 1,
                  title: 'Enter Property Details',
                  description: 'Input your address and current rent amount',
                },
                {
                  step: 2,
                  title: 'Check RPZ Status',
                  description: 'We automatically determine RPZ status and apply correct rules',
                },
                {
                  step: 3,
                  title: 'Get Your Maximum',
                  description: 'See your allowable increase based on current HICP data',
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-purple-600 to-pink-600">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Calculate Your Compliant Rent Review
            </h2>
            <p className="text-lg mb-8 text-purple-100">
              Stay compliant with Irish rental laws and maximize your rental income
            </p>
            <button
              onClick={() => navigate('/landlord-toolkit')}
              className="inline-flex items-center gap-2 bg-white text-purple-600 px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Use Calculator Now
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">
              Rent Review Calculator FAQs
            </h2>
            <div className="space-y-6">
              {faqItems.map((item) => (
                <div
                  key={item.question}
                  className="bg-slate-50 border border-slate-200 rounded-lg p-6 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircleIcon className="w-6 h-6 text-purple-600 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{item.question}</h3>
                      <p className="text-gray-700 mt-2">{item.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RentReviewCalculatorLandingPage;
